import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

import { TABLES, toISODate } from './domain.js';

const GRAPH = 'https://graph.microsoft.com/v1.0';

const SCOPES = [
  'User.Read',
  'Files.ReadWrite',
];

const DATE_FIELDS = new Set([
  'CreatedDate',
  'DueDate',
  'CompletedDate',
  'PlannedStartDate',
  'TargetEndDate',
  'ActualEndDate',
  'WeekStartDate',
  'PlanDate',
  'FollowUpDate',
]);

const DATETIME_FIELDS = new Set([
  'UpdateDateTime',
  'ReminderDateTime',
  'LastCheckedDateTime',
  'SnoozeUntil',
]);

const NUMERIC_FIELDS = new Set([
  'SortOrder',
  'EstimatedHours',
  'ProgressPct',
  'PlanOrder',
  'PlannedHours',
  'ActualHours',
  'ProgressBeforePct',
  'ProgressAfterPct',
]);

export function microsoftConfig(env = import.meta.env) {
  return {
    clientId: String(env.VITE_MS_CLIENT_ID || '').trim(),

    tenantId: String(
      env.VITE_MS_TENANT_ID || 'organizations'
    ).trim(),

    workbookPath: String(
      env.VITE_WORKBOOK_PATH || 'Work_Management_System.xlsx'
    )
      .trim()
      .replace(/^\/+/, '')
      .replace(/\/+$/, ''),

    redirectUri: String(
      env.VITE_MS_REDIRECT_URI || window.location.origin
    )
      .trim()
      .replace(/\/+$/, ''),
  };
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function excelSerialToDate(serial) {
  const milliseconds = Math.round(
    (Number(serial) - 25569) * 86400 * 1000
  );

  const date = new Date(milliseconds);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return [
    date.getUTCFullYear(),
    '-',
    pad2(date.getUTCMonth() + 1),
    '-',
    pad2(date.getUTCDate()),
  ].join('');
}

function excelSerialToDateTime(serial) {
  const milliseconds = Math.round(
    (Number(serial) - 25569) * 86400 * 1000
  );

  const date = new Date(milliseconds);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return (
    `${date.getUTCFullYear()}-` +
    `${pad2(date.getUTCMonth() + 1)}-` +
    `${pad2(date.getUTCDate())}T` +
    `${pad2(date.getUTCHours())}:` +
    `${pad2(date.getUTCMinutes())}`
  );
}

function excelDateSerial(value, includeTime = false) {
  if (
    value === '' ||
    value === null ||
    value === undefined
  ) {
    return '';
  }

  const text = String(value).trim();

  const match = text.match(
    /^([0-9]{4})-([0-9]{2})-([0-9]{2})(?:T([0-9]{2}):([0-9]{2})(?::([0-9]{2}))?)?$/
  );

  if (!match) {
    return value;
  }

  const [
    ,
    yearText,
    monthText,
    dayText,
    hourText = '00',
    minuteText = '00',
    secondText = '00',
  ] = match;

  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);

  const date = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    )
  );

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return value;
  }

  const serial =
    (date.getTime() - Date.UTC(1899, 11, 30)) /
    86400000;

  return includeTime
    ? serial
    : Math.floor(serial + 0.000001);
}

function normalizeCell(field, value) {
  if (value === null || value === undefined) {
    return '';
  }

  if (DATE_FIELDS.has(field)) {
    if (typeof value === 'number') {
      return excelSerialToDate(value);
    }

    return toISODate(value);
  }

  if (DATETIME_FIELDS.has(field)) {
    if (typeof value === 'number') {
      return excelSerialToDateTime(value);
    }

    const text = String(value).trim();

    if (!text) {
      return '';
    }

    if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(text)) {
      return `${text}T00:00`;
    }

    return text.slice(0, 16);
  }

  if (NUMERIC_FIELDS.has(field)) {
    if (value === '') {
      return '';
    }

    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : value;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

function valueForExcel(field, value) {
  if (value === undefined || value === null) {
    return '';
  }

  if (DATE_FIELDS.has(field)) {
    return value === ''
      ? ''
      : excelDateSerial(value, false);
  }

  if (DATETIME_FIELDS.has(field)) {
    return value === ''
      ? ''
      : excelDateSerial(value, true);
  }

  if (NUMERIC_FIELDS.has(field)) {
    if (value === '') {
      return '';
    }

    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : value;
  }

  if (field === 'PlanDateKey') {
    return String(value || '');
  }

  return value;
}

function isInvalidWorkbookSession(errorCode, message) {
  const code = String(errorCode || '');
  const text = String(message || '');

  return (
    /invalidsession|session.*(expired|invalid)|workbooksession.*invalid/i.test(code) ||
    /invalid workbook session|session.*(expired|invalid)|oturum.{0,40}geçersiz/i.test(text)
  );
}

function makeGraphError(payload, response) {
  const apiError = payload?.error || {};

  const innerError =
    apiError?.innerError ||
    apiError?.innererror ||
    {};

  const message =
    apiError?.message ||
    `${response.status} ${response.statusText}`;

  const error = new Error(message);

  error.status = response.status;

  error.code =
    apiError?.code ||
    innerError?.code ||
    '';

  error.requestId =
    innerError?.['request-id'] ||
    innerError?.requestId ||
    '';

  error.clientRequestId =
    innerError?.['client-request-id'] ||
    innerError?.clientRequestId ||
    '';

  error.retryAfter =
    response.headers.get('Retry-After') ||
    '';

  return error;
}

export class MicrosoftExcelProvider {
  constructor(config = microsoftConfig()) {
    if (!config.clientId) {
      throw new Error(
        'Microsoft sign-in is not configured. Check the VITE_MS_CLIENT_ID build variable.'
      );
    }

    if (!config.tenantId) {
      throw new Error(
        'Microsoft sign-in is not configured. Check the VITE_MS_TENANT_ID build variable.'
      );
    }

    if (!config.workbookPath) {
      throw new Error(
        'The Excel workbook path is empty. Check VITE_WORKBOOK_PATH.'
      );
    }

    if (!config.redirectUri) {
      throw new Error(
        'The Microsoft redirect URL is empty. Check VITE_MS_REDIRECT_URI.'
      );
    }

    this.config = config;

    this.msal = new PublicClientApplication({
      auth: {
        clientId: config.clientId,

        authority:
          `https://login.microsoftonline.com/${config.tenantId}`,

        redirectUri: config.redirectUri,

        postLogoutRedirectUri: config.redirectUri,

        navigateToLoginRequestUrl: false,
      },

      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },

      system: {
        allowNativeBroker: false,
      },
    });

    this.account = null;
    this.sessionId = null;

    this.headersByCollection = {};
    this.rowsByCollection = {};

    this.ready = false;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) {
      return this.account;
    }

    await this.msal.initialize();

    const redirectResult =
      await this.msal.handleRedirectPromise();

    this.account =
      redirectResult?.account ||
      this.msal.getActiveAccount() ||
      this.msal.getAllAccounts()[0] ||
      null;

    if (this.account) {
      this.msal.setActiveAccount(this.account);
    }

    this.initialized = true;

    return this.account;
  }

  async login() {
    await this.initialize();

    const response = await this.msal.loginPopup({
      scopes: SCOPES,
    });

    this.account = response.account;

    this.msal.setActiveAccount(this.account);

    this.ready = false;
    this.sessionId = null;

    await this.connectWorkbook();

    return this.account;
  }

  async logout() {
    await this.initialize();

    const account =
      this.account ||
      this.msal.getActiveAccount() ||
      undefined;

    this.account = null;
    this.ready = false;
    this.sessionId = null;

    this.headersByCollection = {};
    this.rowsByCollection = {};

    await this.msal.logoutPopup({
      account,
      postLogoutRedirectUri: this.config.redirectUri,
    });
  }

  async token() {
    await this.initialize();

    if (!this.account) {
      throw new Error(
        'Sign in to Microsoft before accessing the Excel workbook.'
      );
    }

    const request = {
      scopes: SCOPES,
      account: this.account,
    };

    try {
      const response =
        await this.msal.acquireTokenSilent(request);

      return response.accessToken;
    } catch (error) {
      if (
        error instanceof InteractionRequiredAuthError ||
        error?.name === 'InteractionRequiredAuthError'
      ) {
        const response =
          await this.msal.acquireTokenPopup(request);

        this.account =
          response.account || this.account;

        this.msal.setActiveAccount(this.account);

        return response.accessToken;
      }

      throw error;
    }
  }

  async graph(
    path,
    options = {},
    retrySafe = true,
    allowSessionFallback = true
  ) {
    const url = /^https?:\/\//i.test(path)
      ? path
      : `${GRAPH}${path}`;

    const maxAttempts = retrySafe ? 3 : 1;

    let lastError = null;

    for (
      let attempt = 0;
      attempt < maxAttempts;
      attempt += 1
    ) {
      const token = await this.token();

      const headers = new Headers(
        options.headers || {}
      );

      headers.set(
        'Authorization',
        `Bearer ${token}`
      );

      headers.set(
        'Accept',
        'application/json'
      );

      if (
        options.body !== undefined &&
        options.body !== null &&
        !headers.has('Content-Type')
      ) {
        headers.set(
          'Content-Type',
          'application/json'
        );
      }

      if (this.sessionId) {
        headers.set(
          'workbook-session-id',
          this.sessionId
        );
      }

      let response;

      try {
        response = await fetch(url, {
          ...options,
          headers,
        });
      } catch (networkError) {
        lastError = networkError;

        if (
          !retrySafe ||
          attempt >= maxAttempts - 1
        ) {
          throw networkError;
        }

        await new Promise(resolve =>
          setTimeout(
            resolve,
            400 * (2 ** attempt)
          )
        );

        continue;
      }

      if (response.ok) {
        if (response.status === 204) {
          return null;
        }

        return await response
          .json()
          .catch(() => null);
      }

      const payload = await response
        .json()
        .catch(() => ({}));

      const error = makeGraphError(
        payload,
        response
      );

      if (
        allowSessionFallback &&
        this.sessionId &&
        isInvalidWorkbookSession(
          error.code,
          error.message
        )
      ) {
        this.sessionId = null;

        return this.graph(
          path,
          options,
          false,
          false
        );
      }

      const canRetryStatus = [
        429,
        502,
        503,
        504,
      ].includes(response.status);

      if (
        retrySafe &&
        canRetryStatus &&
        attempt < maxAttempts - 1
      ) {
        const retryAfterSeconds =
          Number(error.retryAfter);

        const delayMs =
          Number.isFinite(retryAfterSeconds) &&
          retryAfterSeconds > 0
            ? retryAfterSeconds * 1000
            : 500 * (2 ** attempt);

        lastError = error;

        await new Promise(resolve =>
          setTimeout(
            resolve,
            Math.min(delayMs, 6000)
          )
        );

        continue;
      }

      throw error;
    }

    throw (
      lastError ||
      new Error(
        'Microsoft Graph request failed without a response.'
      )
    );
  }

  workbookBase() {
    const encodedPath = this.config.workbookPath
      .split('/')
      .filter(Boolean)
      .map(segment => encodeURIComponent(segment))
      .join('/');

    return (
      `/me/drive/root:/${encodedPath}:/workbook`
    );
  }

  async connectWorkbook() {
    const base = this.workbookBase();

    try {
      const session = await this.graph(
        `${base}/createSession`,
        {
          method: 'POST',
          body: JSON.stringify({
            persistChanges: true,
          }),
        },
        false
      );

      this.sessionId = session?.id || null;
    } catch {
      this.sessionId = null;
    }

    const tablesResponse = await this.graph(
      `${base}/tables?$select=id,name`
    );

    const existingTableNames = new Set(
      (tablesResponse?.value || []).map(
        table => table.name
      )
    );

    const requiredTables = Object.values(
      TABLES
    ).map(spec => spec.table);

    const missingTables = requiredTables.filter(
      name => !existingTableNames.has(name)
    );

    if (missingTables.length > 0) {
      this.ready = false;

      throw new Error(
        `The workbook was found, but these Excel Tables are missing: ${missingTables.join(', ')}. ` +
        'Check the exact table names in Work_Management_System.xlsx.'
      );
    }

    await this.loadAll();

    this.ready = true;

    return true;
  }

  async fetchAllPages(path) {
    const allRows = [];

    let next = path;

    while (next) {
      const page = await this.graph(next);

      allRows.push(
        ...(page?.value || [])
      );

      next =
        page?.['@odata.nextLink'] ||
        null;
    }

    return allRows;
  }

  async loadTable(collection) {
    const spec = TABLES[collection];

    if (!spec) {
      throw new Error(
        `Unknown data table collection: ${collection}.`
      );
    }

    const base = this.workbookBase();

    const tableRef =
      `tables('${spec.table}')`;

    const headerResult = await this.graph(
      `${base}/${tableRef}/headerRowRange`
    );

    const headers =
      headerResult?.values?.[0]?.map(
        value => String(value)
      ) || [];

    if (headers.length === 0) {
      throw new Error(
        `Could not read the header row for Excel Table ${spec.table}.`
      );
    }

    const missingColumns =
      spec.headers.filter(
        header => !headers.includes(header)
      );

    if (missingColumns.length > 0) {
      throw new Error(
        `${spec.table} is missing required column(s): ${missingColumns.join(', ')}. ` +
        'Do not rename table headers without updating the app schema.'
      );
    }

    const rows = await this.fetchAllPages(
      `${base}/${tableRef}/rows?$top=500`
    );

    const mappedRows = rows.map(
      (row, fallbackIndex) => {
        const values =
          row?.values?.[0] || [];

        const numericIndex =
          Number(row?.index);

        const item = {
          _rowIndex:
            Number.isInteger(numericIndex) &&
            numericIndex >= 0
              ? numericIndex
              : fallbackIndex,
        };

        headers.forEach((header, index) => {
          item[header] = normalizeCell(
            header,
            values[index]
          );
        });

        return item;
      }
    );

    this.headersByCollection[collection] =
      headers;

    this.rowsByCollection[collection] =
      mappedRows;

    return mappedRows;
  }

  async loadAll() {
    const collections = Object.keys(TABLES);

    const entries = await Promise.all(
      collections.map(async collection => [
        collection,
        await this.loadTable(collection),
      ])
    );

    return Object.fromEntries(entries);
  }

  async getData() {
    if (!this.ready) {
      await this.connectWorkbook();
    }

    return this.loadAll();
  }

  rowValues(collection, record) {
    const spec = TABLES[collection];

    if (!spec) {
      throw new Error(
        `Unknown data table collection: ${collection}.`
      );
    }

    const headers =
      this.headersByCollection[collection] ||
      spec.headers;

    return headers.map(header =>
      valueForExcel(
        header,
        record[header]
      )
    );
  }

  async save(collection, record) {
    if (!this.ready) {
      await this.connectWorkbook();
    }

    const spec = TABLES[collection];

    if (!spec) {
      throw new Error(
        `Unknown data table collection: ${collection}.`
      );
    }

    if (
      !record ||
      record[spec.key] === undefined ||
      record[spec.key] === null ||
      String(record[spec.key]).trim() === ''
    ) {
      throw new Error(
        `Cannot save ${spec.table}: the key field ${spec.key} is empty.`
      );
    }

    const currentRows =
      await this.loadTable(collection);

    const keyValue =
      String(record[spec.key]);

    const existing = currentRows.find(
      row =>
        String(row[spec.key] ?? '') ===
        keyValue
    );

    const values = [
      this.rowValues(collection, record),
    ];

    const base = this.workbookBase();

    const tableRef =
      `tables('${spec.table}')`;

    if (
      existing &&
      Number.isInteger(existing._rowIndex) &&
      existing._rowIndex >= 0
    ) {
      const rowPath =
        `${base}/${tableRef}/rows/$/itemAt(index=${existing._rowIndex})`;

      await this.graph(
        rowPath,
        {
          method: 'PATCH',
          body: JSON.stringify({
            values,
          }),
        },
        false
      );
    } else {
      await this.graph(
        `${base}/${tableRef}/rows`,
        {
          method: 'POST',
          body: JSON.stringify({
            index: null,
            values,
          }),
        },
        false
      );
    }

    return this.loadTable(collection);
  }

  async remove(collection, keyValue) {
    if (!this.ready) {
      await this.connectWorkbook();
    }

    const spec = TABLES[collection];

    if (!spec) {
      throw new Error(
        `Unknown data table collection: ${collection}.`
      );
    }

    const rows =
      await this.loadTable(collection);

    const existing = rows.find(
      row =>
        String(row[spec.key] ?? '') ===
        String(keyValue ?? '')
    );

    if (!existing) {
      throw new Error(
        `Could not find ${keyValue} in ${spec.table}. Refresh the data and try again.`
      );
    }

    if (
      !Number.isInteger(existing._rowIndex) ||
      existing._rowIndex < 0
    ) {
      throw new Error(
        `Could not determine the Excel row index for ${keyValue} in ${spec.table}.`
      );
    }

    const rowPath =
      `${this.workbookBase()}/tables('${spec.table}')/rows/$/itemAt(index=${existing._rowIndex})`;

    await this.graph(
      rowPath,
      {
        method: 'DELETE',
      },
      false
    );

    return this.loadTable(collection);
  }

  async refresh() {
    return this.loadAll();
  }
}

export function mapGraphError(error) {
  const message = String(
    error?.message ||
    error ||
    'Unknown error'
  );

  const code = String(
    error?.code || ''
  );

  if (
    /ApiNotFound|apiNotFound/i.test(code) ||
    /API you are trying to use could not be found/i.test(message)
  ) {
    return (
      'Excel rejected the table-row API request. ' +
      'Ensure the latest Cloudflare deployment is active; ' +
      'the app must use rows/$/itemAt(index=...) for updates and deletes.'
    );
  }

  if (
    /InvalidSessionReCreatable|invalidsession|session.*(expired|invalid)/i.test(
      `${code} ${message}`
    )
  ) {
    return (
      'The Excel session expired. The app retried without the session. ' +
      'If the save did not complete, refresh the data and try again.'
    );
  }

  if (
    /itemNotFound|Resource not found|ItemNotFound/i.test(
      `${code} ${message}`
    )
  ) {
    return (
      'The workbook or table was not found. ' +
      'Check the VITE_WORKBOOK_PATH value and the exact Excel table names.'
    );
  }

  if (
    /Forbidden|Access denied|Authorization_RequestDenied|403/i.test(
      `${code} ${message}`
    )
  ) {
    return (
      'Microsoft denied access. Check that Files.ReadWrite is approved for this account and workbook.'
    );
  }

  if (
    /InvalidAuthenticationToken|Unauthorized|401/i.test(
      `${code} ${message}`
    )
  ) {
    return (
      'Microsoft sign-in expired or is no longer valid. Sign in again and retry.'
    );
  }

  if (
    /Hedef oturum geçersiz|Kullanılmaya çalıştığınız API|Kullanmaya çalıştığınız API|oturum.{0,40}geçersiz/i.test(
      message
    )
  ) {
    return (
      'Microsoft Excel rejected the request. ' +
      'Refresh the connection and try again. ' +
      'If it repeats, open the latest deployment log and share the error details.'
    );
  }

  const requestId = error?.requestId
    ? ` (Request ID: ${error.requestId})`
    : '';

  return `${message}${requestId}`;
}
